import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { Row, Col, Modal, Spinner } from 'react-bootstrap'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FaArrowLeft,
  FaFileAlt,
  FaFileInvoice,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaInfoCircle,
  FaTruck,
  FaUser,
  FaCheckCircle,
  FaDownload,
  FaHistory,
  FaWeightHanging,
  FaEdit,
  FaEye,
  FaReply,
  FaDollarSign,
  FaChevronRight,
  FaBan,
  FaSearch,
  FaTimes,
} from 'react-icons/fa'
import {
  fetchRequestDetails,
  fetchTruckNomination,
  clearNomination,
  acceptCounterProposal,
  reviseProposal,
  updateNomination,
  cancelNomination,
} from '../../store/slices/transportRequestSlice'
import {
  approveBilty,
  downloadBiltyPDF,
  generateBiltyPDF,
} from '../../store/slices/biltySlice'
import { fetchDrivers } from '../../store/slices/driverSlice'
import api from '../../services/api'
import { GET_BILTIES, GLOBAL_SEARCH_TRUCK } from '../../helpers/url_helper'
import { convertQuantity } from '../../helpers/unitConversion'
import SubmitProposal from './SubmitProposal'
import NominateTruck from './NominateTruck'
import './TransportRequest.scss'

const fade = {
  hidden: { opacity: 0, y: 12 },
  visible: i => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.05, duration: 0.32, ease: 'easeOut' }
  })
}

const formatDate = date => {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const formatDateTime = date => {
  if (!date) return '—'
  return new Date(date).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDateRange = (fromDate, toDate) => {
  if (fromDate && toDate) {
    return `${formatDate(fromDate)} to ${formatDate(toDate)}`
  }

  if (fromDate) return formatDate(fromDate)
  if (toDate) return formatDate(toDate)

  return '—'
}

const RequestDetails = () => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { selectedRequest, loading, nomination } = useSelector(s => s.transportRequest)
  const { drivers = [] } = useSelector(s => s.driver)

  const [submitProposalModal, setSubmitProposalModal] = useState(false)
  const [nominateTruckModal, setNominateTruckModal] = useState(false)
  const [acceptCounterModal, setAcceptCounterModal] = useState(false)
  const [acceptingProposal, setAcceptingProposal] = useState(null)
  const [acceptanceNotes, setAcceptanceNotes] = useState('')
  const [isAccepting, setIsAccepting] = useState(false)

  const [reviseModal, setReviseModal] = useState(false)
  const [revisingProposal, setRevisingProposal] = useState(null)
  const [isRevising, setIsRevising] = useState(false)
  const [reviseForm, setReviseForm] = useState({
    transport_rate: '',
    rate_unit: '',
    advance_percentage: '',
    advance_amount: '',
    total_freight_cost: '',
    payment_terms: '',
    revision_notes: '',
  })

  const [nominationEditModal, setNominationEditModal] = useState(false)
  const [editingNomination, setEditingNomination] = useState(null)
  const [isUpdatingNomination, setIsUpdatingNomination] = useState(false)
  const [isCancellingNomination, setIsCancellingNomination] = useState(false)
  const [nominationEditError, setNominationEditError] = useState('')
  const [nominationTruckSearchQuery, setNominationTruckSearchQuery] = useState('')
  const [nominationTruckSearching, setNominationTruckSearching] = useState(false)
  const [nominationTruckSearchResult, setNominationTruckSearchResult] = useState(null)
  const [selectedNominationTruck, setSelectedNominationTruck] = useState(null)
  const [selectedNominationDriver, setSelectedNominationDriver] = useState(null)
  const [nominationEditForm, setNominationEditForm] = useState({
    loading_capacity: '',
  })
  const [biltyByNomination, setBiltyByNomination] = useState({})
  const [biltyActionState, setBiltyActionState] = useState({
    approve: {},
    generate: {},
    download: {},
  })
  const [isBiltyLoading, setIsBiltyLoading] = useState(false)
  const [biltyError, setBiltyError] = useState('')

  useEffect(() => {
    dispatch(fetchRequestDetails(id))
    dispatch(fetchTruckNomination(id))
    dispatch(fetchDrivers({ page: 1, limit: 100 }))
    return () => {
      dispatch(clearNomination())
    }
  }, [dispatch, id])

  const fetchNominationBilties = async () => {
    if (!id) return
    setIsBiltyLoading(true)
    setBiltyError('')
    try {
      const listResponse = await api.get(`${GET_BILTIES}?transport_request_id=${id}&page=1&limit=100`)
      const bilties = listResponse.data?.data || []
      const map = {}
      if (Array.isArray(bilties)) {
        bilties.forEach(item => {
          const nominationId =
            item?.nomination_id ||
            item?.nomination?.id ||
            item?.nomination?.nomination_id ||
            item?.truck_nomination_id

          if (!nominationId) return
          const key = String(nominationId)
          if (!map[key]) {
            map[key] = []
          }
          map[key].push(item)
        })

        Object.keys(map).forEach(key => {
          map[key].sort((a, b) => {
            const aDate = new Date(a?.updated_at || a?.created_at || 0).getTime()
            const bDate = new Date(b?.updated_at || b?.created_at || 0).getTime()
            return bDate - aDate
          })
        })
      }

      setBiltyByNomination(map)
    } catch (error) {
      setBiltyByNomination({})
      const status = error.response?.status
      const message = error.response?.data?.message || error.response?.data?.error || ''
      const isNoBiltyCase =
        status === 404 ||
        message.toLowerCase().includes('not found') ||
        message.toLowerCase().includes('no bilty')
      setBiltyError(isNoBiltyCase ? '' : (message || 'Unable to fetch bilty for this request.'))
    } finally {
      setIsBiltyLoading(false)
    }
  }

  useEffect(() => {
    fetchNominationBilties()
  }, [id])

  const getNominationBilty = nominationItem => {
    const nominationId = nominationItem?.id || nominationItem?.nomination_id
    if (!nominationId) return null
    const list = biltyByNomination[String(nominationId)] || []
    return list.length ? list[0] : null
  }

  const setBiltyActionLoading = (action, nominationId, value) => {
    if (!nominationId) return
    setBiltyActionState(prev => ({
      ...prev,
      [action]: {
        ...prev[action],
        [String(nominationId)]: value,
      },
    }))
  }

  const handleApproveBilty = async nominationItem => {
    const bilty = getNominationBilty(nominationItem)
    const nominationId = nominationItem?.id || nominationItem?.nomination_id
    if (!bilty?.id || !nominationId) return

    setBiltyActionLoading('approve', nominationId, true)
    try {
      await dispatch(approveBilty(bilty.id)).unwrap()
      fetchNominationBilties()
    } catch (error) {
      // handled in slice
    } finally {
      setBiltyActionLoading('approve', nominationId, false)
    }
  }

  const handleGenerateBiltyPdf = async nominationItem => {
    const bilty = getNominationBilty(nominationItem)
    const nominationId = nominationItem?.id || nominationItem?.nomination_id
    if (!bilty?.id || !nominationId) return

    setBiltyActionLoading('generate', nominationId, true)
    try {
      await dispatch(generateBiltyPDF(bilty.id)).unwrap()
      fetchNominationBilties()
    } catch (error) {
      // handled in slice
    } finally {
      setBiltyActionLoading('generate', nominationId, false)
    }
  }

  const handleDownloadBiltyPdf = async nominationItem => {
    const bilty = getNominationBilty(nominationItem)
    const nominationId = nominationItem?.id || nominationItem?.nomination_id
    if (!bilty?.id || !nominationId) return

    setBiltyActionLoading('download', nominationId, true)
    try {
      await dispatch(downloadBiltyPDF(bilty.id)).unwrap()
    } catch (error) {
      // handled in slice
    } finally {
      setBiltyActionLoading('download', nominationId, false)
    }
  }

  const handleRevise = async () => {
    if (!revisingProposal) return
    setIsRevising(true)
    try {
      const revisionData = {
        transport_rate: parseFloat(reviseForm.transport_rate),
        rate_unit: reviseForm.rate_unit,
        advance_percentage: parseFloat(reviseForm.advance_percentage),
        advance_amount: parseFloat(reviseForm.advance_amount),
        total_freight_cost: parseFloat(reviseForm.total_freight_cost),
      }
      if (reviseForm.payment_terms?.trim()) revisionData.payment_terms = reviseForm.payment_terms.trim()
      if (reviseForm.revision_notes?.trim()) revisionData.revision_notes = reviseForm.revision_notes.trim()
      await dispatch(reviseProposal({ requestId: id, proposalId: revisingProposal.id, revisionData })).unwrap()
      setReviseModal(false)
      setRevisingProposal(null)
      dispatch(fetchRequestDetails(id))
    } catch (e) {
      // error handled in slice
    } finally {
      setIsRevising(false)
    }
  }

  const handleAcceptCounter = async () => {
    if (!acceptingProposal) return
    setIsAccepting(true)
    try {
      await dispatch(acceptCounterProposal({
        requestId: id,
        proposalId: acceptingProposal.id,
        acceptanceNotes,
      })).unwrap()
      setAcceptCounterModal(false)
      setAcceptingProposal(null)
      setAcceptanceNotes('')
      dispatch(fetchRequestDetails(id))
    } catch (e) {
      // error handled in slice
    } finally {
      setIsAccepting(false)
    }
  }

  // ── helpers ────────────────────────────────────────────────────────────────
  const getStatusBadge = status => {
    const map = {
      invited:            'blue',
      viewed:             'blue',
      proposal_submitted: 'yellow',
      accepted:           'green',
      negotiating:        'purple',
    }
    return (
      <span className={`trl-badge trl-badge--${map[status] || 'gray'}`}>
        {status?.replace(/_/g, ' ')}
      </span>
    )
  }

  const getProposalStatusBadge = status => {
    const map = {
      pending:          'yellow',
      submitted:        'blue',
      accepted:         'green',
      rejected:         'red',
      counter_proposed: 'purple',
      revised:          'gray',
      negotiating:      'purple',
    }
    return (
      <span className={`trl-badge trl-badge--${map[status] || 'gray'}`}>
        {status?.replace(/_/g, ' ')}
      </span>
    )
  }

  const canSubmitProposal = () =>
    ['invited', 'viewed'].includes(selectedRequest?.my_proposal_status)

  const canNominateTruck = () =>
    selectedRequest?.my_proposal_status === 'accepted' &&
    selectedRequest?.my_proposals?.length > 0

  const getAcceptedProposalId = () => {
    const p = selectedRequest?.my_proposals?.find(p => p.status === 'accepted')
    return p?.id || null
  }

  const hasProposalSubmitted = () =>
    ['proposal_submitted', 'negotiating', 'accepted'].includes(selectedRequest?.my_proposal_status)
  const refreshNominationData = () => {
    dispatch(fetchRequestDetails(id))
    dispatch(fetchTruckNomination(id))
    fetchNominationBilties()
  }

  const openEditNomination = item => {
    setEditingNomination(item)
    setNominationEditError('')
    setNominationTruckSearchQuery(item?.truck?.truck_number || '')
    setNominationTruckSearchResult(null)
    setSelectedNominationTruck(item?.truck ? {
      ...item.truck,
      truck_id: item.truck.id,
    } : null)
    setSelectedNominationDriver(item?.driver ? {
      ...item.driver,
      id: item.driver.id,
      name: item.driver.driver_name,
    } : null)
    setNominationEditForm({
      loading_capacity: item?.truck?.loading_capacity ?? '',
    })
    setNominationEditModal(true)
  }

  const closeEditNominationModal = () => {
    setNominationEditModal(false)
    setEditingNomination(null)
    setNominationEditError('')
    setNominationTruckSearchQuery('')
    setNominationTruckSearchResult(null)
    setSelectedNominationTruck(null)
    setSelectedNominationDriver(null)
    setNominationEditForm({ loading_capacity: '' })
  }

  const handleSearchNominationTruck = async () => {
    if (!nominationTruckSearchQuery.trim()) return
    setNominationTruckSearching(true)
    setNominationTruckSearchResult(null)
    setNominationEditError('')
    try {
      const response = await api.get(`${GLOBAL_SEARCH_TRUCK}/${nominationTruckSearchQuery.trim().toUpperCase()}`)
      const truck = response.data?.data || response.data?.truck || response.data
      if (truck && (truck.truck_id || truck.id)) {
        setNominationTruckSearchResult(truck)
      } else {
        setNominationEditError('No truck found with this registration number.')
      }
    } catch (error) {
      setNominationEditError(error.response?.data?.error || 'Truck not found in the system.')
    } finally {
      setNominationTruckSearching(false)
    }
  }

  const handleNominationDriverSelect = driverId => {
    const driver = drivers.find(d => String(d.driver_id || d.id || d._id) === String(driverId))
    if (!driver) return
    setSelectedNominationDriver({
      ...driver,
      id: driver.id || driver.driver_id,
      name: driver.name || driver.driver_name,
      driver_name: driver.driver_name || driver.name,
      driver_phone: driver.driver_phone || driver.phone || driver.mobile,
    })
  }

  const handleUpdateNomination = async () => {
    if (!editingNomination) return
    const proposalId = editingNomination.proposal?.id || getAcceptedProposalId()
    if (!proposalId) return

    if (!selectedNominationTruck?.id && !selectedNominationTruck?.truck_id) {
      setNominationEditError('Please search and select a truck.')
      return
    }

    if (!selectedNominationDriver?.id && !selectedNominationDriver?.driver_id) {
      setNominationEditError('Please select a driver.')
      return
    }

    if (!nominationEditForm.loading_capacity || parseFloat(nominationEditForm.loading_capacity) <= 0) {
      setNominationEditError('Please enter valid loading capacity.')
      return
    }

    if (parseFloat(nominationEditForm.loading_capacity) > nominationEditMaxCapacity) {
      setNominationEditError(`Loading capacity cannot exceed ${formatQuantity(nominationEditMaxCapacity)} ${quantityUnit}.`)
      return
    }

    setIsUpdatingNomination(true)
    try {
      await dispatch(updateNomination({
        requestId: id,
        proposalId,
        nominationId: editingNomination.id,
        nominationData: {
          truck_id: parseInt(selectedNominationTruck.id || selectedNominationTruck.truck_id),
          driver_id: parseInt(selectedNominationDriver.id || selectedNominationDriver.driver_id),
          loading_capacity: parseFloat(nominationEditForm.loading_capacity),
        },
      })).unwrap()
      closeEditNominationModal()
      refreshNominationData()
    } catch (error) {
      // handled by slice
    } finally {
      setIsUpdatingNomination(false)
    }
  }

  const handleCancelNomination = async item => {
    const proposalId = item?.proposal?.id || getAcceptedProposalId()
    if (!proposalId || !window.confirm('Cancel this nomination?')) return

    setIsCancellingNomination(true)
    try {
      await dispatch(cancelNomination({
        requestId: id,
        proposalId,
        nominationId: item.id,
      })).unwrap()
      refreshNominationData()
    } catch (error) {
      // handled by slice
    } finally {
      setIsCancellingNomination(false)
    }
  }

  // ── loading ────────────────────────────────────────────────────────────────
  if (loading || !selectedRequest) {
    return (
      <div className="trl-loading">
        <Spinner animation="border" size="sm" style={{ color: 'rgb(20,35,115)' }} />
        <span>Loading request details…</span>
      </div>
    )
  }

  const req = selectedRequest
  const proposalHistory = req.my_proposals?.length > 0 ? req.my_proposals : (req.proposals || [])
  const nominationList = nomination?.nominations?.length
    ? nomination.nominations
    : nomination?.nomination
      ? [nomination.nomination]
      : []
  const quantityTracker = nomination?.quantity_tracker || null
  const quantityUnit = quantityTracker?.quantity_unit || req.quantity_unit || ''
  const requestQuantityValue = parseFloat(quantityTracker?.request_quantity ?? req.total_quantity ?? 0) || 0
  const activeNominations = nominationList.filter(
    item => item.status !== 'CANCELLED' && !item.cancellation
  )
  const nominatedQuantityValue = activeNominations.reduce(
    (sum, item) => sum + (parseFloat(item.truck?.loading_capacity || 0) || 0),
    0
  )
  const remainingQuantityValue = Math.max(requestQuantityValue - nominatedQuantityValue, 0)
  const quantityMet = remainingQuantityValue <= 0

  const formatQuantity = value => {
    if (!Number.isFinite(value)) return '—'
    return Number.isInteger(value) ? String(value) : value.toFixed(2)
  }
  const currentEditingCapacity = parseFloat(editingNomination?.truck?.loading_capacity || 0) || 0
  const nominationEditMaxCapacity = remainingQuantityValue + currentEditingCapacity
  const routeLabel = `${req.origin || 'Origin pending'} to ${req.destination || 'Destination pending'}`
  const calculateRevisedTotals = values => {
    const rate = parseFloat(values.transport_rate) || 0
    const quantity = parseFloat(req.total_quantity) || 0
    const quantityUnit = req.quantity_unit || ''
    const normalizedRateUnit = values.rate_unit?.trim() || ''

    const totalFreight =
      normalizedRateUnit === 'Per Trip' || normalizedRateUnit === 'Trip'
        ? rate
        : rate * convertQuantity(quantity, quantityUnit, normalizedRateUnit)

    const advPercent = parseFloat(values.advance_percentage) || 0
    const advanceAmount = (totalFreight * advPercent) / 100

    return {
      ...values,
      total_freight_cost: totalFreight > 0 ? totalFreight.toFixed(2) : '',
      advance_amount: advanceAmount > 0 ? advanceAmount.toFixed(2) : '',
    }
  }
  const kpiItems = [
    {
      label: 'Total Quantity',
      value: req.total_quantity ? `${req.total_quantity} ${req.quantity_unit || ''}`.trim() : '—',
      note: 'Declared for this movement',
      mod: 'blue',
    },
    {
      label: 'Loading Date',
      value: formatDateRange(req.loading_date_from || req.loading_date, req.loading_date_to),
      note: 'Planned dispatch window',
      mod: 'green',
    },
    {
      label: 'Current Stage',
      value: req.status?.replace(/_/g, ' ') || 'Pending',
      note: 'Request lifecycle status',
      mod: 'yellow',
    },
  ]

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <AnimatePresence>
      <motion.div
        className="rd-page"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
      >

        {/* ── PAGE HEADER ─────────────────────────────────────────────────── */}
        <motion.div className="rd-header" custom={0} variants={fade}>
          <button className="rd-back-btn" onClick={() => navigate('/transport-request')}>
            <FaArrowLeft />
          </button>
          <div className="rd-header-icon">
            <FaFileAlt />
          </div>
          <div className="rd-header-text">
            <h1 className="rd-title">{req.request_number || 'Transport Request'}</h1>
            <p className="rd-sub">
              Transport Request Details • {routeLabel}
              {req.contract_number ? ` • Contract ${req.contract_number}` : ''}
            </p>
          </div>
          <div className="ms-auto">
            {getStatusBadge(req.status)}
          </div>
        </motion.div>

        <Row className="g-3 mb-3">
          {kpiItems.map((kpi, index) => (
            <Col xs={12} sm={6} lg={3} key={kpi.label}>
              <motion.div className={`rd-kpi rd-kpi--${kpi.mod}`} custom={index + 1} variants={fade}>
                <span className="rd-kpi-label">{kpi.label}</span>
                <span className="rd-kpi-value">{kpi.value}</span>
                <span className="rd-kpi-note">{kpi.note}</span>
              </motion.div>
            </Col>
          ))}
        </Row>

        {/* ── TWO-COLUMN LAYOUT ────────────────────────────────────────────── */}
        <Row className="g-4">

          {/* ── LEFT COL ────────────────────────────────────────────────── */}
          <Col lg={8}>

            {/* Request Info */}
            <motion.div className="rd-section" custom={1} variants={fade}>
              <div className="rd-section-title">
                <FaFileAlt className="rd-section-icon" />
                Transport Request Details
              </div>

              <div className="rd-route-banner">
                <div className="rd-route-point rd-route-point--from">
                  <span className="rd-route-dot" />
                  <div>
                    <span className="rd-route-lbl">Origin</span>
                    <span className="rd-route-val">{req.origin || '—'}</span>
                  </div>
                </div>
                <span className="rd-route-line" />
                <div className="rd-route-point rd-route-point--to">
                  <span className="rd-route-dot" />
                  <div>
                    <span className="rd-route-lbl">Destination</span>
                    <span className="rd-route-val">{req.destination || '—'}</span>
                  </div>
                </div>
              </div>

              <div className="rd-detail-grid">
                <div className="rd-detail-item">
                  <span className="rd-detail-lbl"><FaCalendarAlt className="me-1" />Loading Date</span>
                  <span className="rd-detail-val">
                    {formatDateRange(req.loading_date_from || req.loading_date, req.loading_date_to)}
                  </span>
                </div>
                <div className="rd-detail-item">
                  <span className="rd-detail-lbl"><FaDollarSign className="me-1" />Freight Payable By</span>
                  <span className="rd-detail-val">{req.freight_payable_by || '—'}</span>
                </div>
                <div className="rd-detail-item">
                  <span className="rd-detail-lbl"><FaWeightHanging className="me-1" />Total Quantity</span>
                  <span className="rd-detail-val">{req.total_quantity ? `${req.total_quantity} ${req.quantity_unit || ''}` : '—'}</span>
                </div>
                <div className="rd-detail-item">
                  <span className="rd-detail-lbl"><FaFileAlt className="me-1" />Contract Number</span>
                  <span className="rd-detail-val rd-detail-val--accent">{req.contract_number || '—'}</span>
                </div>
              </div>
            </motion.div>

            <motion.div className="rd-section" custom={2} variants={fade}>
              <div className="rd-section-title">
                <FaUser className="rd-section-icon" />
                Business Parties
              </div>
              <Row className="g-3 p-3">
                <Col md={6}>
                  <div className="rd-entity-card">
                    <span className="rd-entity-lbl">Seller Entity</span>
                    <span className="rd-entity-name">{req.seller_entity_name || '—'}</span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="rd-entity-card">
                    <span className="rd-entity-lbl">Buyer Entity</span>
                    <span className="rd-entity-name">{req.buyer_entity_name || '—'}</span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="rd-entity-card">
                    <span className="rd-entity-lbl">Requesting Party</span>
                    <span className="rd-entity-name">{req.creator_entity?.entity_name || '—'}</span>
                    <span className="rd-entity-meta">Role: {req.creator_entity?.role || '—'}</span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="rd-entity-card">
                    <span className="rd-entity-lbl">Requesting Company</span>
                    <span className="rd-entity-name">{req.contractor_company_name || '—'}</span>
                  </div>
                </Col>
              </Row>
            </motion.div>

            {/* Commodity Details */}
            {req.commodities?.length > 0 && (
              <motion.div className="rd-section" custom={3} variants={fade}>
                <div className="rd-section-title">
                  <FaWeightHanging className="rd-section-icon" />
                  Commodity Details
                </div>
                <div className="rd-table-wrap">
                  <table className="rd-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Commodity</th>
                        <th>Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {req.commodities.map((c, i) => (
                        <tr key={c.commodity_detail_id || i}>
                          <td>{i + 1}</td>
                          <td className="rd-table-bold">{c.commodity_name}</td>
                          <td>{c.quantity}  {c.quantity_unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Additional Notes */}
            {req.notes && (
              <motion.div className="rd-section" custom={4} variants={fade}>
                <div className="rd-section-title">
                  <FaInfoCircle className="rd-section-icon" />
                  Additional Notes
                </div>
                <p className="rd-notes">{req.notes}</p>
              </motion.div>
            )}

            {/* Negotiation History */}
            <motion.div className="rd-section" custom={5} variants={fade}>
                <div className="rd-section-title">
                  <FaHistory className="rd-section-icon" />
                  Negotiation History
                </div>

                {proposalHistory.length === 0 ? (
                  <div className="rd-empty-block">
                    <p className="rd-empty-title">No proposals submitted</p>
                    <p className="rd-empty-sub">Submit a professional proposal to begin negotiation for this request.</p>
                  </div>
                ) : (
                  <div className="rd-proposals-list">
                    {proposalHistory.map((proposal, i) => {
                    const isContractorCounter =
                      proposal.status === 'submitted' && proposal.proposed_by === 'contractor'
                    return (
                      <div
                        key={proposal.id || proposal.proposal_id || i}
                        className={`rd-proposal-card${isContractorCounter ? ' rd-proposal-card--counter' : ''}`}
                      >
                        <div className="rd-proposal-card-head">
                          <div>
                            <span className="rd-proposal-version">Proposal Version {proposal.version}</span>
                            <span className="rd-proposal-meta">
                              {proposal.proposed_by === 'contractor' ? 'Contractor System' : 'Authorized Personnel'}
                              {' • '}
                              {proposal.submitted_at
                                ? formatDate(proposal.submitted_at)
                                : '—'}
                            </span>
                          </div>
                          <div className="rd-proposal-actions">
                            {getProposalStatusBadge(proposal.status)}
                            {isContractorCounter && (
                              <div className="rd-proposal-btns">
                                <button
                                  className="rd-action-btn rd-action-btn--accept"
                                  onClick={() => {
                                    setAcceptingProposal(proposal)
                                    setAcceptanceNotes('')
                                    setAcceptCounterModal(true)
                                  }}
                                >
                                  <FaCheckCircle className="me-1" />Accept
                                </button>
                                <button
                                  className="rd-action-btn rd-action-btn--revise"
                                  onClick={() => {
                                    setRevisingProposal(proposal)
                                    setReviseForm(calculateRevisedTotals({
                                      transport_rate: proposal.transport_rate || '',
                                      rate_unit: proposal.rate_unit || '',
                                      advance_percentage: proposal.advance_percentage || '',
                                      advance_amount: proposal.advance_amount || '',
                                      total_freight_cost: proposal.total_freight_cost || '',
                                      payment_terms: proposal.payment_terms || '',
                                      revision_notes: '',
                                    }))
                                    setReviseModal(true)
                                  }}
                                >
                                  <FaEdit className="me-1" />Revise
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {isContractorCounter && (
                          <div className="rd-counter-alert">
                            <FaReply className="me-2" />
                            Contractor has issued a counter offer. Action required.
                          </div>
                        )}

                        <div className="rd-proposal-stats">
                          <div className="rd-stat">
                            <span className="rd-stat-lbl">Rate Protocol</span>
                            <span className="rd-stat-val">₹{proposal.transport_rate || '—'} / {proposal.rate_unit || 'unit'}</span>
                          </div>
                          <div className="rd-stat">
                            <span className="rd-stat-lbl">Total Freight</span>
                            <span className="rd-stat-val rd-stat-val--accent">₹{proposal.total_freight_cost || '—'}</span>
                          </div>
                          <div className="rd-stat">
                            <span className="rd-stat-lbl">Advance</span>
                            <span className="rd-stat-val">₹{proposal.advance_amount || '—'}</span>
                          </div>
                          <div className="rd-stat">
                            <span className="rd-stat-lbl">Advance %</span>
                            <span className="rd-stat-val">{proposal.advance_percentage ? `${proposal.advance_percentage}%` : '—'}</span>
                          </div>
                        </div>
                      </div>
                    )
                    })}
                  </div>
                )}
              </motion.div>

            {/* Truck Nomination Info */}
            {nominationList.length > 0 && (
              <motion.div className="rd-section" custom={6} variants={fade}>
                <div className="rd-section-title">
                  <FaTruck className="rd-section-icon rd-section-icon--green" />
                  Truck Nominations
                  <span className="trl-badge trl-badge--green ms-auto">{nominationList.length} nominated</span>
                </div>

                {quantityTracker && (
                  <div className="rd-nomination-track">
                    <div className="rd-track-item">
                      <span className="rd-track-lbl">Request Quantity</span>
                      <span className="rd-track-val">{formatQuantity(requestQuantityValue)} {quantityUnit}</span>
                    </div>
                    <div className="rd-track-item">
                      <span className="rd-track-lbl">Nominated Quantity</span>
                      <span className="rd-track-val rd-track-val--green">{formatQuantity(nominatedQuantityValue)} {quantityUnit}</span>
                    </div>
                    <div className="rd-track-item">
                      <span className="rd-track-lbl">Remaining Quantity</span>
                      <span className="rd-track-val rd-track-val--amber">{formatQuantity(remainingQuantityValue)} {quantityUnit}</span>
                    </div>
                    <div className="rd-track-item">
                      <span className="rd-track-lbl">Fulfilment</span>
                      <span className="rd-track-val">{quantityMet ? 'Quantity met' : 'Pending quantity'}</span>
                    </div>
                  </div>
                )}

                <div className="rd-nomination-list">
                  {nominationList.map((item, index) => (
                    <div className="rd-nomination-card" key={item.id || index}>
                      {(() => {
                        const nominationId = item?.id || item?.nomination_id
                        const nominationBilty = getNominationBilty(item)
                        const isApproveLoading = !!biltyActionState.approve[String(nominationId)]
                        const isGenerateLoading = !!biltyActionState.generate[String(nominationId)]
                        const isDownloadLoading = !!biltyActionState.download[String(nominationId)]
                        const canApproveNominationBilty =
                          nominationBilty?.bilty_type === 'online' && !nominationBilty?.transporter_approved

                        return (
                          <>
                      <div className="rd-nomination-card-head">
                        <span className="rd-nomination-title">Nomination {index + 1}</span>
                        <div className="rd-nomination-head-actions">
                          <span className={`trl-badge trl-badge--${item.status === 'NOMINATED' ? 'green' : 'gray'}`}>
                            {item.status?.replace(/_/g, ' ') || 'status unavailable'}
                          </span>
                          {item.status !== 'CANCELLED' && (
                            <>
                              <button className="rd-nomination-action-btn" type="button" onClick={() => openEditNomination(item)}>
                                <FaEdit /> Edit
                              </button>
                              <button className="rd-nomination-action-btn rd-nomination-action-btn--danger" type="button" onClick={() => handleCancelNomination(item)} disabled={isCancellingNomination}>
                                <FaBan /> Cancel
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      <Row className="g-3">
                        <Col md={6}>
                          <div className="rd-entity-card">
                            <span className="rd-entity-lbl"><FaTruck className="me-1" />Vehicle</span>
                            <span className="rd-entity-name">{item.truck?.truck_number || '—'}</span>
                            <span className="rd-entity-meta">
                              Loading: {item.truck?.loading_capacity || '—'}
                              {item.truck?.truck_type ? ` • ${item.truck.truck_type}` : ''}
                            </span>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="rd-entity-card">
                            <span className="rd-entity-lbl"><FaUser className="me-1" />Driver</span>
                            <span className="rd-entity-name">{item.driver?.driver_name || '—'}</span>
                            <span className="rd-entity-meta">{item.driver?.driver_phone || '—'}</span>
                          </div>
                        </Col>
                      </Row>

                      <div className="rd-nomination-meta">
                        <span>Type: {item.nomination_type || '—'}</span>
                        <span>Updated: {formatDateTime(item.updated_at)}</span>
                        <span>Proposal: {item.proposal?.proposal_number || '—'}</span>
                      </div>

                      <div className="rd-nomination-bilty">
                        <div className="rd-nomination-bilty-head">
                          <span className="rd-entity-lbl"><FaFileInvoice className="me-1" />Bilty Actions</span>
                          {nominationBilty?.bilty_number && (
                            <span className="rd-nomination-bilty-number">{nominationBilty.bilty_number}</span>
                          )}
                        </div>

                        {nominationBilty?.id ? (
                          <div className="rd-nomination-bilty-actions">
                            <button
                              type="button"
                              className="rd-nomination-action-btn"
                              onClick={() => navigate(`/bilty/${nominationBilty.id}`)}
                            >
                              <FaEye /> View
                            </button>

                            {canApproveNominationBilty && (
                              <button
                                type="button"
                                className="rd-nomination-action-btn"
                                onClick={() => handleApproveBilty(item)}
                                disabled={isApproveLoading}
                              >
                                {isApproveLoading ? <Spinner animation="border" size="sm" /> : <FaCheckCircle />}
                                {isApproveLoading ? 'Approving...' : 'Approve'}
                              </button>
                            )}

                            <button
                              type="button"
                              className="rd-nomination-action-btn"
                              onClick={() => handleGenerateBiltyPdf(item)}
                              disabled={isGenerateLoading}
                            >
                              {isGenerateLoading ? <Spinner animation="border" size="sm" /> : <FaFileAlt />}
                              {isGenerateLoading ? 'Generating...' : 'Generate PDF'}
                            </button>

                            <button
                              type="button"
                              className="rd-nomination-action-btn"
                              onClick={() => handleDownloadBiltyPdf(item)}
                              disabled={isDownloadLoading || !nominationBilty?.pdf_url}
                            >
                              {isDownloadLoading ? <Spinner animation="border" size="sm" /> : <FaDownload />}
                              {isDownloadLoading ? 'Downloading...' : 'Download PDF'}
                            </button>
                          </div>
                        ) : (
                          <p className="rd-nomination-bilty-empty">Bilty not generated yet for this nomination.</p>
                        )}
                      </div>
                          </>
                        )
                      })()}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

          </Col>

          {/* ── SIDEBAR ─────────────────────────────────────────────────── */}
          <Col lg={4}>
            <motion.div className="rd-sidebar" custom={6} variants={fade}>

              {/* Actions Card */}
              <div className="rd-sidebar-card">
                <div className="rd-sidebar-card-title">Quick Actions</div>

                {/* Primary CTA buttons */}
                {canSubmitProposal() && (
                  <button
                    className="rd-sidebar-btn rd-sidebar-btn--primary"
                    onClick={() => setSubmitProposalModal(true)}
                  >
                    <span className="rd-btn-icon"><FaEdit /></span>
                    Submit Proposal
                    <FaChevronRight className="ms-auto" style={{ fontSize: '0.7rem', opacity: 0.7 }} />
                  </button>
                )}

                {canNominateTruck() && (
                  <button
                    className="rd-sidebar-btn rd-sidebar-btn--primary"
                    onClick={() => navigate(`/transport-request/${id}/nominate`)}
                  >
                    <span className="rd-btn-icon"><FaTruck /></span>
                    Nominate Vehicle
                    <FaChevronRight className="ms-auto" style={{ fontSize: '0.7rem', opacity: 0.7 }} />
                  </button>
                )}

                {isBiltyLoading && (
                  <div className="rd-sidebar-empty">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Checking nomination bilty status...
                  </div>
                )}

                {biltyError && (
                  <p className="rd-sidebar-empty">{biltyError}</p>
                )}

                {/* Divider before secondary links */}
                {(hasProposalSubmitted() || req.truck_nomination) &&
                  (canSubmitProposal() || canNominateTruck()) && (
                  <div className="rd-sidebar-divider" />
                )}

                {/* Secondary navigation buttons */}
                {hasProposalSubmitted() && (
                  <button
                    className="rd-sidebar-btn rd-sidebar-btn--secondary"
                    onClick={() => navigate(`/transport-request/${id}/proposals`)}
                  >
                    <span className="rd-btn-icon"><FaHistory /></span>
                    Proposal History
                    <FaChevronRight className="ms-auto" style={{ fontSize: '0.7rem', opacity: 0.5 }} />
                  </button>
                )}

                {req.truck_nomination && (
                  <button
                    className="rd-sidebar-btn rd-sidebar-btn--secondary"
                    onClick={() => navigate(`/transport-request/${id}/nomination`)}
                  >
                    <span className="rd-btn-icon"><FaEye /></span>
                    Review Nomination
                    <FaChevronRight className="ms-auto" style={{ fontSize: '0.7rem', opacity: 0.5 }} />
                  </button>
                )}

                {!canSubmitProposal() && !hasProposalSubmitted() && !canNominateTruck() && !req.truck_nomination && (
                  <p className="rd-sidebar-empty">No actions available at this stage.</p>
                )}
              </div>

              {/* Request Summary */}
              <div className="rd-sidebar-card">
                <div className="rd-sidebar-card-title">Request Summary</div>
                <div className="rd-summary-rows">
                  <div className="trl-view-row">
                    <div className="trl-view-lbl">Status</div>
                    <div className="trl-view-val">{getStatusBadge(req.status)}</div>
                  </div>
                  <div className="trl-view-row">
                    <div className="trl-view-lbl">Request No.</div>
                    <div className="trl-view-val">{req.request_number || '—'}</div>
                  </div>
                  {req.contract_number && (
                    <div className="trl-view-row">
                      <div className="trl-view-lbl">Contract No.</div>
                      <div className="trl-view-val">{req.contract_number}</div>
                    </div>
                  )}
                  {(req.loading_date_from || req.loading_date_to || req.loading_date) && (
                    <div className="trl-view-row">
                      <div className="trl-view-lbl">Loading Date</div>
                      <div className="trl-view-val">
                        {formatDateRange(req.loading_date_from || req.loading_date, req.loading_date_to)}
                      </div>
                    </div>
                  )}
                  {req.total_quantity && (
                    <div className="trl-view-row">
                      <div className="trl-view-lbl">Quantity</div>
                      <div className="trl-view-val">{req.total_quantity} {req.quantity_unit}</div>
                    </div>
                  )}
                  <div className="trl-view-row">
                    <div className="trl-view-lbl">Created On</div>
                    <div className="trl-view-val">{formatDateTime(req.created_at)}</div>
                  </div>
                </div>
              </div>

            </motion.div>
          </Col>
        </Row>

        {/* ── CHILD COMPONENT MODALS ───────────────────────────────────────── */}
        <SubmitProposal
          isOpen={submitProposalModal}
          toggle={() => setSubmitProposalModal(false)}
          requestId={id}
          requestData={req}
        />

        <NominateTruck
          isOpen={nominateTruckModal}
          toggle={() => setNominateTruckModal(false)}
          requestId={id}
          proposalId={getAcceptedProposalId()}
          requestData={req}
        />

        {/* ── REVISE MODAL ─────────────────────────────────────────────────── */}
        <Modal
          show={reviseModal}
          onHide={() => !isRevising && setReviseModal(false)}
          centered
          size="md"
        >
          <Modal.Header closeButton className="trl-modal-header">
            <Modal.Title className="trl-modal-title">Revise Proposal</Modal.Title>
          </Modal.Header>
          <Modal.Body className="trl-modal-body">
            {revisingProposal && (
              <div className="rd-counter-info mb-3">
                <small>Contractor rate: ₹{revisingProposal.transport_rate} / {revisingProposal.rate_unit}</small>
              </div>
            )}
            <Row className="g-3">
              <Col md={6}>
                <div className="rd-form-group">
                  <label className="rd-form-lbl">Rate</label>
                  <input
                    className="rd-form-input"
                    type="number"
                    placeholder="e.g. 27"
                    value={reviseForm.transport_rate}
                    onChange={e => setReviseForm(f => calculateRevisedTotals({ ...f, transport_rate: e.target.value }))}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="rd-form-group">
                  <label className="rd-form-lbl">Rate Unit</label>
                  <input
                    className="rd-form-input"
                    type="text"
                    placeholder="Rate unit"
                    value={reviseForm.rate_unit}
                    readOnly
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="rd-form-group">
                  <label className="rd-form-lbl">Total Freight</label>
                  <input
                    className="rd-form-input"
                    type="number"
                    placeholder="e.g. 72500"
                    value={reviseForm.total_freight_cost}
                    readOnly
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="rd-form-group">
                  <label className="rd-form-lbl">Advance %</label>
                  <input
                    className="rd-form-input"
                    type="number"
                    placeholder="e.g. 28"
                    value={reviseForm.advance_percentage}
                    onChange={e => setReviseForm(f => calculateRevisedTotals({ ...f, advance_percentage: e.target.value }))}
                  />
                </div>
              </Col>
              <Col md={12}>
                <div className="rd-form-group">
                  <label className="rd-form-lbl">Advance Amount</label>
                  <input
                    className="rd-form-input"
                    type="number"
                    value={reviseForm.advance_amount}
                    readOnly
                  />
                </div>
              </Col>
              <Col md={12}>
                <div className="rd-form-group">
                  <label className="rd-form-lbl">Revision Notes</label>
                  <textarea
                    className="rd-form-input"
                    rows={3}
                    value={reviseForm.revision_notes}
                    readOnly
                  />
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="trl-modal-footer">
            <button
              className="trl-btn-cancel"
              onClick={() => setReviseModal(false)}
              disabled={isRevising}
            >
              Cancel
            </button>
            <button
              className="trl-btn-submit"
              onClick={handleRevise}
              disabled={
                isRevising ||
                !reviseForm.transport_rate ||
                !reviseForm.rate_unit ||
                !reviseForm.total_freight_cost ||
                !reviseForm.advance_percentage ||
                !reviseForm.advance_amount
              }
            >
              {isRevising ? <Spinner animation="border" size="sm" className="me-2" /> : null}
              Submit Revision
            </button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={nominationEditModal}
          onHide={() => !isUpdatingNomination && closeEditNominationModal()}
          centered
          size="md"
        >
          <Modal.Header closeButton className="trl-modal-header">
            <Modal.Title className="trl-modal-title">Edit Nomination</Modal.Title>
          </Modal.Header>
          <Modal.Body className="trl-modal-body">
            {editingNomination && (
              <div className="rd-counter-info mb-3">
                <small>
                  Update nomination using truck search and driver selection. Current allocation: {formatQuantity(currentEditingCapacity)} {quantityUnit}
                </small>
              </div>
            )}

            <Row className="g-3 mb-3">
              <Col md={12}>
                <div className="rd-form-group">
                  <label className="rd-form-lbl">Search Vehicle (Registration Number)</label>
                  <div className="rd-edit-search-row">
                    <input
                      className="rd-form-input"
                      type="text"
                      value={nominationTruckSearchQuery}
                      placeholder="e.g. MH12AB1234"
                      onChange={e => setNominationTruckSearchQuery(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleSearchNominationTruck())}
                      disabled={!!selectedNominationTruck}
                    />
                    {!selectedNominationTruck ? (
                      <button
                        type="button"
                        className="trl-btn-submit rd-edit-search-btn"
                        onClick={handleSearchNominationTruck}
                        disabled={nominationTruckSearching || !nominationTruckSearchQuery.trim()}
                      >
                        {nominationTruckSearching ? <Spinner animation="border" size="sm" /> : <><FaSearch className="me-1" />Search</>}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="trl-btn-cancel rd-edit-search-btn"
                        onClick={() => {
                          setSelectedNominationTruck(null)
                          setNominationTruckSearchResult(null)
                          setNominationTruckSearchQuery('')
                        }}
                      >
                        <FaTimes className="me-1" />Change
                      </button>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            {nominationTruckSearchResult && (
              <div
                className="rd-edit-select-card mb-3"
                onClick={() => {
                  setSelectedNominationTruck(nominationTruckSearchResult)
                  setNominationTruckSearchResult(null)
                  setNominationTruckSearchQuery(nominationTruckSearchResult.truck_number || nominationTruckSearchResult.truckNumber || '')
                }}
              >
                <span className="rd-edit-select-title">{nominationTruckSearchResult.truck_number || nominationTruckSearchResult.truckNumber}</span>
                <span className="rd-edit-select-sub">
                  {nominationTruckSearchResult.truck_type || 'Truck type unavailable'}
                  {nominationTruckSearchResult.truck_capacity ? ` • Capacity ${nominationTruckSearchResult.truck_capacity}` : ''}
                </span>
              </div>
            )}

            {selectedNominationTruck && (
              <div className="rd-edit-selected-card mb-3">
                <span className="rd-edit-select-title">Selected Vehicle: {selectedNominationTruck.truck_number || selectedNominationTruck.truckNumber || '—'}</span>
                <span className="rd-edit-select-sub">
                  {selectedNominationTruck.truck_type || 'Truck type unavailable'}
                  {selectedNominationTruck.truck_capacity ? ` • Capacity ${selectedNominationTruck.truck_capacity}` : ''}
                </span>
              </div>
            )}

            <Row className="g-3">
              <Col md={12}>
                <div className="rd-form-group">
                  <label className="rd-form-lbl">Select Driver</label>
                  <select
                    className="rd-form-input"
                    value={selectedNominationDriver?.id || selectedNominationDriver?.driver_id || ''}
                    onChange={e => handleNominationDriverSelect(e.target.value)}
                  >
                    <option value="">-- Choose Driver --</option>
                    {drivers.map(driver => (
                      <option key={driver.id || driver.driver_id} value={driver.id || driver.driver_id}>
                        {(driver.name || driver.driver_name)} ({driver.license_number || driver.drivingLicenseNumber || 'No license'})
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
              <Col md={12}>
                <div className="rd-form-group">
                  <label className="rd-form-lbl">Loading Capacity</label>
                  <input
                    className="rd-form-input"
                    type="number"
                    min="0.01"
                    max={nominationEditMaxCapacity || undefined}
                    value={nominationEditForm.loading_capacity}
                    onChange={e => setNominationEditForm(f => ({ ...f, loading_capacity: e.target.value }))}
                  />
                  <span className="rd-edit-select-sub mt-1">Maximum allowed: {formatQuantity(nominationEditMaxCapacity)} {quantityUnit}</span>
                </div>
              </Col>
            </Row>
            {selectedNominationDriver && (
              <div className="rd-edit-selected-card mt-3">
                <span className="rd-edit-select-title">Selected Driver: {selectedNominationDriver.driver_name || selectedNominationDriver.name || '—'}</span>
                <span className="rd-edit-select-sub">{selectedNominationDriver.driver_phone || selectedNominationDriver.phone || selectedNominationDriver.mobile || '—'}</span>
              </div>
            )}

            {nominationEditError ? <div className="rd-edit-error mt-3">{nominationEditError}</div> : null}
          </Modal.Body>
          <Modal.Footer className="trl-modal-footer">
            <button
              className="trl-btn-cancel"
              onClick={closeEditNominationModal}
              disabled={isUpdatingNomination}
            >
              Cancel
            </button>
            <button
              className="trl-btn-submit"
              onClick={handleUpdateNomination}
              disabled={
                isUpdatingNomination ||
                !selectedNominationTruck ||
                !selectedNominationDriver ||
                !nominationEditForm.loading_capacity
              }
            >
              {isUpdatingNomination ? <Spinner animation="border" size="sm" className="me-2" /> : null}
              Save Changes
            </button>
          </Modal.Footer>
        </Modal>

        {/* ── ACCEPT COUNTER MODAL ─────────────────────────────────────────── */}
        <Modal
          show={acceptCounterModal}
          onHide={() => !isAccepting && setAcceptCounterModal(false)}
          centered
          size="md"
        >
          <Modal.Header closeButton className="trl-modal-header">
            <Modal.Title className="trl-modal-title">Accept Counter Proposal</Modal.Title>
          </Modal.Header>
          <Modal.Body className="trl-modal-body">
            {acceptingProposal && (
              <div className="rd-accept-summary mb-3">
                <div className="trl-view-row">
                  <div className="trl-view-lbl">Rate</div>
                  <div className="trl-view-val rd-rate-highlight">
                    ₹{acceptingProposal.transport_rate} / {acceptingProposal.rate_unit}
                  </div>
                </div>
              </div>
            )}
            <div className="rd-form-group">
              <label className="rd-form-lbl">Acceptance Notes</label>
              <textarea
                className="rd-form-input"
                rows={3}
                value={acceptanceNotes}
                onChange={e => setAcceptanceNotes(e.target.value)}
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="trl-modal-footer">
            <button
              className="trl-btn-cancel"
              onClick={() => setAcceptCounterModal(false)}
              disabled={isAccepting}
            >
              Cancel
            </button>
            <button
              className="trl-btn-submit"
              onClick={handleAcceptCounter}
              disabled={isAccepting}
            >
              {isAccepting ? <Spinner animation="border" size="sm" className="me-2" /> : null}
              Confirm Acceptance
            </button>
          </Modal.Footer>
        </Modal>

      </motion.div>
    </AnimatePresence>
  )
}


export default RequestDetails
